import { useChat } from '@ai-sdk/react';
import { usePostHog } from '@posthog/react';
import { DefaultChatTransport } from 'ai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { aiChatApi, useDeleteAiChatThreadMutation, useLazyGetAiChatThreadMessagesQuery } from 'store/api/aiChatApi';
import { APIExperienceRecordsSlice } from 'store/api/experience-records';
import { AiChatThreadDetail, AiChatThreadSummary, PortalChatMessage, TitleDataPart } from 'types/aiChat';
import { z } from 'zod';
import { clearResumeRecord, touchResumeRecord } from '../aiChatResume';
import { getErrorMessageKey, getErrorMessageKeyFromBody } from '../constants/errorMessages';
import { getLatestUserText, mapThreadMessagesToUiMessages } from '../utils/messageMapping';
import { getToolName, isToolPart, type ToolPart } from '../utils/toolCallParts';

/** A send that failed: the line for what went wrong, and whether the request got no response at all. */
export interface AiChatFailedSend {
    message: string;
    noResponse: boolean;
}

const threadDataPartSchema = z.object({
    threadId: z.union([z.string(), z.number()]),
    isNew: z.boolean().optional(),
    title: z.string().optional(),
    userMessageId: z.number().optional(),
});

export type AiChatSurface = 'page' | 'floating';

/**
 * Where a conversation runs: one of the assistant's own two surfaces, or the Home widget creator, whose
 * conversation is thrown away with its dialog and so is never listed among the assistant's threads nor resumed.
 */
export type AiChatControllerSurface = AiChatSurface | 'widget-creator';

export interface AiChatRequestPageContext {
    objectType: string;
    id: string | null;
    url: string;
}

/** A record the user picked into a ready-made prompt's slot, sent with the message as already resolved. */
export interface AiChatRequestRecord {
    type: 'contact' | 'group' | 'case';
    id: string;
    label: string;
}

export interface AiChatSendOptions {
    records?: AiChatRequestRecord[];
}

interface UseAiChatControllerParams {
    apiBaseUrl: string;
    // Surface reported to analytics when a call does not name one. A session shared between the
    // full page and the floating panel names it per call instead — see useAiChatSurface.
    surface: AiChatControllerSurface;
    // Optional page identity (object type + id + url) attached to each turn as `pageContext`.
    // Read from a ref at send time so the latest page attaches even while the panel stays open.
    pageContext?: AiChatRequestPageContext | null;
    dataPartHandler?: (dataPart: { type: string; data: unknown }) => boolean;
}

const HEARTBEAT_WARNING_MS = 25_000;
const HEARTBEAT_TIMEOUT_MS = 45_000;
const HEARTBEAT_INTERVAL_MS = 5_000;
const CHAT_STREAM_UI_THROTTLE_MS = 50;
const STREAM_INTERRUPTED_ERROR_KEY = 'ai-chat.error.stream-interrupted';

export function useAiChatController({ apiBaseUrl, surface, pageContext, dataPartHandler }: UseAiChatControllerParams) {
    const intl = useIntl();
    const dispatch = useDispatch();
    const posthog = usePostHog();

    // The surface whose history this conversation belongs to; none for a conversation the assistant does not keep.
    const historySurface = surface === 'widget-creator' ? null : surface;

    const [threadId, setThreadId] = useState<number | null>(null);
    const threadIdRef = useRef<number | null>(null);
    const pageContextRef = useRef<AiChatRequestPageContext | null>(pageContext ?? null);
    pageContextRef.current = pageContext ?? null;
    const clientThreadIdRef = useRef<string | null>(null);
    // The prompt id the last send carried, for the thread row the stream then creates.
    const sentPromptIdRef = useRef<string | null>(null);
    // The records the last send carried, which a retry of it carries again.
    const sentRecordsRef = useRef<AiChatRequestRecord[]>([]);
    const lastClientMessageIdRef = useRef<string | null>(null);
    // The failed question Edit message took back: the next send reuses its id, so the server replaces that turn.
    const editedClientMessageIdRef = useRef<string | null>(null);
    const sendInFlightRef = useRef(false);
    const lastHeartbeatRef = useRef(0);
    const connectionWarningShownRef = useRef(false);
    const watchdogTimedOutRef = useRef(false);
    // The surface and start time of the answer a send or retry is waiting on, for the event its end sends.
    const answerStartRef = useRef<{ surface: AiChatControllerSurface; startedAt: number } | null>(null);
    const [watchdogErrorKey, setWatchdogErrorKey] = useState<string | null>(null);
    // No chunk for a while in a running turn; the surfaces show a banner while it holds.
    const [isConnectionUnstable, setIsConnectionUnstable] = useState(false);
    // The answer the user stopped, for the transcript to say so and offer a retry.
    const [stoppedMessageId, setStoppedMessageId] = useState<string | null>(null);
    // The failure a retry is answering: its notice stays, busy, until that retry ends.
    const [retriedFailure, setRetriedFailure] = useState<AiChatFailedSend | null>(null);
    const failedSendRef = useRef<AiChatFailedSend | null>(null);
    // The ready-made prompt a new chat starts from, sent with its first message only.
    const startingPromptIdRef = useRef<string | null>(null);
    // The title the stream gave a thread: one started in this session has no fetched detail to read it from.
    const [streamedTitle, setStreamedTitle] = useState<{ threadId: number; title: string } | null>(null);

    const [triggerMessages, { data: threadDetail, isFetching, isError, originalArgs: fetchedThreadId }] =
        useLazyGetAiChatThreadMessagesQuery();
    // Which thread's history has already been written into this session's messages. The two
    // sessions share RTK Query's cache, so the other one selecting the same thread refetches the
    // entry both are subscribed to — without this, that refetch would replay history over a
    // conversation this session is in the middle of.
    const appliedHistoryThreadIdRef = useRef<number | null>(null);
    const [deleteThreadApi] = useDeleteAiChatThreadMutation();

    useEffect(() => {
        threadIdRef.current = threadId;
        // A thread becoming current re-arms this surface's resume window; opening the surface
        // within it lands back in the conversation instead of on a fresh chat.
        if (threadId != null && historySurface) {
            touchResumeRecord(historySurface, threadId);
        }
    }, [historySurface, threadId]);

    const transport = useMemo(
        () =>
            new DefaultChatTransport<PortalChatMessage>({
                api: `${apiBaseUrl}/v1/ai/chat/stream`,
                credentials: 'include',
                prepareSendMessagesRequest: ({ messages, body }) => {
                    const rawBody = (body || {}) as Record<string, unknown>;
                    const message = getLatestUserText(messages as PortalChatMessage[]);

                    return {
                        body: {
                            message,
                            ...(rawBody.threadId ? { threadId: String(rawBody.threadId) } : {}),
                            ...(rawBody.clientThreadId ? { clientThreadId: rawBody.clientThreadId } : {}),
                            ...(rawBody.clientMessageId ? { clientMessageId: rawBody.clientMessageId } : {}),
                            ...(rawBody.pageContext ? { pageContext: rawBody.pageContext } : {}),
                            ...(rawBody.promptId ? { promptId: rawBody.promptId } : {}),
                            ...(rawBody.records ? { records: rawBody.records } : {}),
                        },
                    };
                },
            }),
        [apiBaseUrl]
    );

    const { messages, status, error, sendMessage, stop, regenerate, setMessages, clearError } =
        useChat<PortalChatMessage>({
            transport,
            experimental_throttle: CHAT_STREAM_UI_THROTTLE_MS,
            onData: (dataPart) => {
                lastHeartbeatRef.current = Date.now();

                if (dataPartHandler?.(dataPart)) return;

                if (dataPart.type === 'data-thread') {
                    const parsed = threadDataPartSchema.safeParse(dataPart.data);
                    if (!parsed.success) return;
                    const data = parsed.data;
                    const newThreadId = Number(data.threadId);

                    if (!Number.isNaN(newThreadId) && threadIdRef.current == null) {
                        setThreadId(newThreadId);
                        threadIdRef.current = newThreadId;
                        clientThreadIdRef.current = null;

                        if (historySurface) {
                            dispatch(
                                aiChatApi.util.updateQueryData('getAiChatThreads', 0, (draft) => {
                                    const existingThread = draft.find((t) => t.id === newThreadId);
                                    if (!existingThread) {
                                        draft.unshift({
                                            id: newThreadId,
                                            title: data.title || '',
                                            lastMessageAt: new Date().toISOString(),
                                            description: null,
                                            kind: null,
                                            promptId: sentPromptIdRef.current,
                                        });
                                    }
                                })
                            );
                        }
                    }

                    if (data.title && !Number.isNaN(newThreadId)) {
                        setStreamedTitle({ threadId: newThreadId, title: data.title });
                        dispatch(
                            aiChatApi.util.updateQueryData('getAiChatThreads', 0, (draft) => {
                                const existingThread = draft.find((t) => t.id === newThreadId);
                                if (existingThread) {
                                    existingThread.title = data.title || existingThread.title;
                                }
                            })
                        );
                    }
                    return;
                }

                if (dataPart.type === 'data-title') {
                    const data = dataPart.data as TitleDataPart;
                    const titleThreadId = Number(data.threadId);
                    if (Number.isNaN(titleThreadId)) return;

                    const { title, description, kind, promptId } = data;
                    if (title) {
                        setStreamedTitle({ threadId: titleThreadId, title });
                    }
                    // The list's row and the open thread's detail, wherever either is cached.
                    const applySummary = (thread: AiChatThreadSummary | AiChatThreadDetail) => {
                        thread.title = title || thread.title;
                        thread.description = description ?? thread.description;
                        thread.kind = kind ?? thread.kind;
                        thread.promptId = promptId ?? thread.promptId;
                    };
                    dispatch(
                        aiChatApi.util.updateQueryData('getAiChatThreads', 0, (draft) => {
                            const existingThread = draft.find((t) => t.id === titleThreadId);
                            if (existingThread) {
                                applySummary(existingThread);
                            }
                        })
                    );
                    dispatch(aiChatApi.util.updateQueryData('getAiChatThreadMessages', titleThreadId, applySummary));
                }
            },
            onFinish: ({ message, isAbort, isDisconnect, isError, finishReason }) => {
                setRetriedFailure(null);
                if (isAbort && message?.role === 'assistant') {
                    setStoppedMessageId(message.id);
                }
                // A draft the assistant saved is a write the experience record lists' cache never saw.
                if (
                    message?.parts
                        .filter(isToolPart)
                        .some((part) => getToolName(part as unknown as ToolPart) === 'createExperienceRecordDraft')
                ) {
                    dispatch(APIExperienceRecordsSlice.util.invalidateTags(['ExperienceRecordListItems']));
                }
                // Defensive fallback: if stream closes without a completion reason and without
                // an explicit abort/disconnect/error signal, treat it as interrupted even when
                // no assistant tokens were received yet.
                const streamEndedWithoutCompletion = !isAbort && !isDisconnect && !isError && finishReason == null;

                if (streamEndedWithoutCompletion) {
                    setWatchdogErrorKey(STREAM_INTERRUPTED_ERROR_KEY);
                }

                const answerStart = answerStartRef.current;
                answerStartRef.current = null;
                if (answerStart) {
                    // The watchdog ends a silent stream with stop(), so its abort is a failure, not the user stopping.
                    const watchdogStopped = watchdogTimedOutRef.current;
                    const failed =
                        isError ||
                        isDisconnect ||
                        watchdogStopped ||
                        finishReason === 'error' ||
                        streamEndedWithoutCompletion;
                    posthog.capture('ai_answer_finished', {
                        surface: answerStart.surface,
                        outcome: isAbort && !watchdogStopped ? 'aborted' : failed ? 'error' : 'completed',
                        // The SDK gives no finish reason for an error or an abort.
                        finish_reason: finishReason ?? 'none',
                        duration_ms: Date.now() - answerStart.startedAt,
                    });
                }
            },
        });

    const isStreaming = status === 'submitted' || status === 'streaming';

    useEffect(() => {
        if (!isStreaming || messages.length === 0) return;
        lastHeartbeatRef.current = Date.now();
    }, [isStreaming, messages]);

    useEffect(() => {
        if (!isStreaming) return;

        lastHeartbeatRef.current = Date.now();
        connectionWarningShownRef.current = false;
        watchdogTimedOutRef.current = false;

        const interval = window.setInterval(() => {
            const elapsed = Date.now() - lastHeartbeatRef.current;

            if (elapsed > HEARTBEAT_TIMEOUT_MS && !watchdogTimedOutRef.current) {
                watchdogTimedOutRef.current = true;
                stop();
                setWatchdogErrorKey('ai-chat.error.connection-timeout');
                return;
            }

            if (elapsed > HEARTBEAT_WARNING_MS && !connectionWarningShownRef.current) {
                connectionWarningShownRef.current = true;
                setIsConnectionUnstable(true);
            } else if (elapsed <= HEARTBEAT_WARNING_MS && connectionWarningShownRef.current) {
                // Chunks are arriving again.
                connectionWarningShownRef.current = false;
                setIsConnectionUnstable(false);
            }
        }, HEARTBEAT_INTERVAL_MS);

        return () => {
            window.clearInterval(interval);
            setIsConnectionUnstable(false);
        };
    }, [isStreaming, stop]);

    useEffect(() => {
        if (!threadDetail?.messages || threadDetail.id !== threadId) return;
        if (appliedHistoryThreadIdRef.current === threadId) return;

        appliedHistoryThreadIdRef.current = threadId;
        const uiMessages = mapThreadMessagesToUiMessages(threadDetail.messages);
        setMessages(uiMessages);
        clearError();
    }, [clearError, setMessages, threadDetail, threadId]);

    // Loading is about this session getting the thread it asked for, not about the shared cache
    // entry being busy: once the right history is in hand, someone else refetching it is not this
    // session's spinner to show.
    const isHistoryFetching = isFetching && threadDetail?.id !== threadId;

    const threadTitle =
        (streamedTitle?.threadId === threadId ? streamedTitle.title : null) ??
        (threadDetail?.id === threadId ? threadDetail.title : null) ??
        '';

    // A lazy query keeps its error until the next trigger, and the assistant's own controllers never
    // unmount, so an old failure would otherwise still read as "the thread on screen is gone".
    const isHistoryError = isError && threadId != null && fetchedThreadId === threadId;

    const buildRequestBody = useCallback((clientMessageIdOverride?: string) => {
        const currentThreadId = threadIdRef.current;

        if (currentThreadId == null && !clientThreadIdRef.current) {
            clientThreadIdRef.current = crypto.randomUUID();
        }

        return {
            ...(currentThreadId != null ? { threadId: currentThreadId } : {}),
            ...(currentThreadId == null && clientThreadIdRef.current
                ? { clientThreadId: clientThreadIdRef.current }
                : {}),
            ...(clientMessageIdOverride ? { clientMessageId: clientMessageIdOverride } : {}),
            ...(pageContextRef.current ? { pageContext: pageContextRef.current } : {}),
            ...(currentThreadId == null && startingPromptIdRef.current
                ? { promptId: startingPromptIdRef.current }
                : {}),
        };
    }, []);

    /**
     * Names the ready-made prompt the next message comes from, or none. Only a new chat's first message carries
     * it; the send takes it either way, so the message after carries none.
     */
    const setStartingPromptId = useCallback((promptId: string | null) => {
        startingPromptIdRef.current = promptId;
    }, []);

    const send = useCallback(
        async (
            rawMessage: string,
            callSurface: AiChatControllerSurface = surface,
            { records = [] }: AiChatSendOptions = {}
        ) => {
            const message = rawMessage.trim();
            if (!message || isStreaming || sendInFlightRef.current) return false;

            sendInFlightRef.current = true;

            try {
                setWatchdogErrorKey(null);
                setStoppedMessageId(null);
                setRetriedFailure(null);
                connectionWarningShownRef.current = false;
                watchdogTimedOutRef.current = false;
                lastHeartbeatRef.current = Date.now();

                const clientMessageId = editedClientMessageIdRef.current ?? crypto.randomUUID();
                editedClientMessageIdRef.current = null;
                lastClientMessageIdRef.current = clientMessageId;

                // Sending into an existing thread re-arms the resume window; a first send has no
                // thread yet and is covered once the stream assigns one.
                const sentThreadId = threadIdRef.current;
                if (sentThreadId != null && historySurface) {
                    touchResumeRecord(historySurface, sentThreadId);
                    // The server sorts the list by last message, but the cached copy is only ever
                    // appended to. Without this the thread keeps the position and timestamp it had
                    // before the send, so a conversation just continued still reads — and offers
                    // itself — as days old.
                    dispatch(
                        aiChatApi.util.updateQueryData('getAiChatThreads', 0, (draft) => {
                            const index = draft.findIndex((thread) => thread.id === sentThreadId);
                            if (index === -1) {
                                return;
                            }
                            const [sentThread] = draft.splice(index, 1);
                            sentThread.lastMessageAt = new Date().toISOString();
                            draft.unshift(sentThread);
                        })
                    );
                }

                posthog.capture('ai_message_sent', {
                    surface: callSurface,
                    thread: threadIdRef.current != null ? 'existing' : 'new',
                    message_length: message.length,
                });
                answerStartRef.current = { surface: callSurface, startedAt: Date.now() };

                const body = { ...buildRequestBody(clientMessageId), ...(records.length > 0 ? { records } : {}) };
                sentPromptIdRef.current = body.promptId ?? null;
                sentRecordsRef.current = records;
                startingPromptIdRef.current = null;
                await sendMessage({ text: message }, { body });

                return true;
            } finally {
                sendInFlightRef.current = false;
            }
        },
        [buildRequestBody, dispatch, historySurface, isStreaming, posthog, sendMessage, surface]
    );

    const stopGeneration = useCallback(
        (callSurface: AiChatControllerSurface = surface) => {
            posthog.capture('ai_generation_stopped', { surface: callSurface });
            void stop();
        },
        [posthog, stop, surface]
    );

    const retryLastMessage = useCallback(
        async (callSurface: AiChatControllerSurface = surface) => {
            if (isStreaming || !lastClientMessageIdRef.current) return;

            setRetriedFailure(failedSendRef.current);
            setStoppedMessageId(null);
            setWatchdogErrorKey(null);
            connectionWarningShownRef.current = false;
            watchdogTimedOutRef.current = false;
            lastHeartbeatRef.current = Date.now();

            posthog.capture('ai_response_regenerated', { surface: callSurface });
            answerStartRef.current = { surface: callSurface, startedAt: Date.now() };

            const body = {
                ...buildRequestBody(lastClientMessageIdRef.current),
                ...(sentRecordsRef.current.length > 0 ? { records: sentRecordsRef.current } : {}),
            };
            await regenerate({
                // A first message that failed before its thread existed goes again with its prompt id.
                body:
                    threadIdRef.current == null && sentPromptIdRef.current
                        ? { ...body, promptId: sentPromptIdRef.current }
                        : body,
            });
        },
        [buildRequestBody, isStreaming, posthog, regenerate, surface]
    );

    const selectThread = useCallback(
        (nextThreadId: number) => {
            stop();
            clearError();
            setWatchdogErrorKey(null);
            setStoppedMessageId(null);
            setRetriedFailure(null);
            connectionWarningShownRef.current = false;
            watchdogTimedOutRef.current = false;
            setThreadId(nextThreadId);
            threadIdRef.current = nextThreadId;
            clientThreadIdRef.current = null;
            lastClientMessageIdRef.current = null;
            editedClientMessageIdRef.current = null;
            appliedHistoryThreadIdRef.current = null;
            triggerMessages(nextThreadId);
        },
        [clearError, stop, triggerMessages]
    );

    const newChat = useCallback(() => {
        stop();
        clearError();
        // Every way a conversation ends on purpose funnels through here — the new-chat buttons,
        // deleting the current thread, a remembered thread that no longer loads — and none of them
        // may be resumed: an explicit new chat must stay new.
        if (historySurface) {
            clearResumeRecord(historySurface);
        }
        setWatchdogErrorKey(null);
        setStoppedMessageId(null);
        setRetriedFailure(null);
        startingPromptIdRef.current = null;
        connectionWarningShownRef.current = false;
        watchdogTimedOutRef.current = false;
        setThreadId(null);
        threadIdRef.current = null;
        clientThreadIdRef.current = null;
        lastClientMessageIdRef.current = null;
        editedClientMessageIdRef.current = null;
        appliedHistoryThreadIdRef.current = null;
        setMessages([]);
    }, [clearError, historySurface, setMessages, stop]);

    const deleteThread = useCallback(
        async (deleteThreadId: number, callSurface: AiChatControllerSurface = surface) => {
            await deleteThreadApi(deleteThreadId).unwrap();
            posthog.capture('ai_thread_deleted', { surface: callSurface });
            if (threadIdRef.current === deleteThreadId) {
                newChat();
            }
        },
        [deleteThreadApi, newChat, posthog, surface]
    );

    const failure = useMemo((): AiChatFailedSend | null => {
        // The stream stalled or closed early: the connection dropped mid-answer.
        if (watchdogErrorKey) {
            return { message: intl.formatMessage({ id: watchdogErrorKey }), noResponse: true };
        }

        if (!error) return null;

        const bodyErrorKey = getErrorMessageKeyFromBody(error.message);
        if (bodyErrorKey) {
            return { message: intl.formatMessage({ id: bodyErrorKey }), noResponse: false };
        }

        const match = error.message.match(/AI_CHAT_HTTP_STATUS:(\d+)/);
        if (match) {
            const statusCode = Number(match[1]);
            const key = getErrorMessageKey(statusCode);
            return { message: intl.formatMessage({ id: key }), noResponse: false };
        }

        // No answer the backend wrote, as when the fetch itself failed.
        return {
            message: error.message || intl.formatMessage({ id: 'ai-chat.error.server-unavailable' }),
            noResponse: true,
        };
    }, [error, intl, watchdogErrorKey]);
    failedSendRef.current = failure;
    const errorMessage = failure?.message ?? null;
    // The failure on screen: the current one, or the one a running retry answers, so its notice stays put.
    const failedSend = failure ?? (isStreaming ? retriedFailure : null);
    const isRetryingFailedSend = failure == null && isStreaming && retriedFailure != null;

    /**
     * Takes the failed turn back: removes the last question and whatever answer it got, and returns the question's
     * text for the composer.
     */
    const editFailedMessage = useCallback(() => {
        const lastUserIndex = messages.findLastIndex((message) => message.role === 'user');
        if (lastUserIndex === -1) return '';
        const text = getLatestUserText(messages);
        clearError();
        setWatchdogErrorKey(null);
        setRetriedFailure(null);
        editedClientMessageIdRef.current = lastClientMessageIdRef.current;
        lastClientMessageIdRef.current = null;
        setMessages(messages.slice(0, lastUserIndex));
        return text;
    }, [clearError, messages, setMessages]);

    const userVisibleMessages = useMemo(() => messages.filter((message) => message.role !== 'system'), [messages]);

    const contentVersion = useMemo(() => {
        return userVisibleMessages.reduce((acc, message) => {
            const partCount = message.parts.length;
            const textLength = message.parts.reduce((partAcc, part) => {
                if (part.type === 'text') return partAcc + part.text.length;
                return partAcc + 1;
            }, 0);
            return acc + message.id.length + partCount + textLength;
        }, 0);
    }, [userVisibleMessages]);

    return {
        threadId,
        threadTitle,
        setThreadId,
        isHistoryFetching,
        isHistoryError,
        isStreaming,
        userVisibleMessages,
        contentVersion,
        error,
        errorMessage,
        failedSend,
        isRetryingFailedSend,
        editFailedMessage,
        connectionUnstable: isStreaming && isConnectionUnstable,
        stoppedMessageId,
        setStartingPromptId,
        send,
        stop,
        stopGeneration,
        retryLastMessage,
        selectThread,
        newChat,
        deleteThread,
        clearError,
    };
}
